/**
 * 관등이 오르거나 장수 레벨이 오르면 편성 화면 위에 축하 장면을 띄운다.
 *
 * 플레이한 사람이 청했다: "관직이 올랐을 때와 레벨업이 되었을 때 성취감을 느낄만한 멋진 비주얼 효과를 보여줘."
 * 예전에는 결과 화면의 한 줄("장수 레벨 5 → 6")이 전부였고, 철수하면 곧장 편성 화면으로 넘어가 오른 줄도 몰랐다.
 *
 * 무엇이 올랐는지는 출정을 시작할 때 떠 둔 모습(`growthSnapshot`)과 돌아온 뒤의 모습을 견줘 안다. 전과는 철수해야
 * 확정되고, 토벌 경험치는 전투가 끝날 때 들어가므로 "시작할 때"가 두 경우 모두의 기준이다.
 */
import { leaveAsking } from './leave-guard.ts';
import { companionLevelOf, heroLevelOf, mateRank, mateSquadSize, metCompanions, rankFor, RANKS, salaryFor, squadSizeFor } from '../meta/army.ts';
import type { Army } from '../meta/army.ts';
import { commandShort } from './labels.ts';
import { skillPointsFor } from '../sim/skills.ts';
import { el } from './dom.ts';

export interface GrowthSnapshot {
  readonly level: number;
  readonly rank: number;
  readonly merit: number;
  readonly companions: ReadonlyMap<string, number>;
  /** 동료마다 관등(`RANKS` 색인)과 이끄는 정원(`mateSquadSize`). 동료도 제 관등이 오른다(Todo8). */
  readonly mateRanks?: ReadonlyMap<string, { readonly rank: number; readonly size: number }>;
}

export function growthSnapshot(army: Army): GrowthSnapshot {
  const companions = new Map<string, number>();
  const mateRanks = new Map<string, { rank: number; size: number }>();
  for (const c of metCompanions(army)) {
    companions.set(c.id, companionLevelOf(army, c.id));
    mateRanks.set(c.id, { rank: RANKS.indexOf(mateRank(army, c.id)), size: mateSquadSize(army, c.id) });
  }
  return { level: heroLevelOf(army), rank: RANKS.indexOf(rankFor(army.totalMerit)), merit: army.totalMerit, companions, mateRanks };
}

/** 축하 장면 한 장. `big`이 가운데 크게 뜨는 글이다. */
export interface Fanfare {
  readonly kind: 'rank' | 'level';
  readonly title: string;
  readonly big: string;
  readonly lines: readonly string[];
}

/**
 * 오른 것을 장면으로. 관등이 먼저다 — 드물고 크다. 동료 레벨은 따로 장면을 두지 않고 장수 레벨 장면(없으면 한 장을
 * 새로)에 줄로 붙인다: 동료가 셋이면 장면이 다섯이 되어 누르다 지친다.
 */
export function fanfaresFor(before: GrowthSnapshot, after: GrowthSnapshot, names: (id: string) => string): Fanfare[] {
  const out: Fanfare[] = [];
  if (after.rank > before.rank) {
    const from = RANKS[before.rank];
    const to = RANKS[after.rank];
    out.push({
      kind: 'rank',
      title: '관등이 올랐다',
      big: to.name,
      lines: [
        `${from.name} → ${to.name}`,
        `내 부대 정원 ${squadSizeFor(before.merit)} → ${squadSizeFor(after.merit)}명`,
        // 관등이 오르면 월급도 오른다(`RANK_SALARY`) — 출정을 철수로 마칠 때 받는다.
        `월급 ${salaryFor(before.merit).toLocaleString()} → ${salaryFor(after.merit).toLocaleString()}냥`,
      ],
    });
  }
  const mates: string[] = [];
  // 자란 동료(사람 수). 한 동료가 레벨과 관등이 함께 올라 줄이 둘이어도 한 명이다.
  const grown = new Set<string>();
  for (const [id, lv] of after.companions) {
    const was = before.companions.get(id);
    if (was !== undefined && lv > was) {
      mates.push(`${names(id)} Lv ${was} → ${lv}`);
      grown.add(id);
    }
  }
  // 동료의 관등이나 정원이 오른 것도 같은 줄에. 동료 분대의 정원은 min(장수 정원, 그 동료의 관등 정원)이다
  // (`mateSquadSize`) — 동료의 관등은 그대로여도 장수의 관등이 올라 천장이 높아지면 정원이 는다(리뷰가 짚었다).
  for (const [id, now] of after.mateRanks ?? []) {
    const was = before.mateRanks?.get(id);
    if (was === undefined) continue;
    if (now.rank > was.rank) {
      // 장수의 정원이 천장이라 관등이 올라도 정원이 그대로일 수 있다 — 그렇다고 적는다.
      mates.push(
        now.size > was.size
          ? `${names(id)} 관등 ${RANKS[now.rank].name} · 정원 ${was.size} → ${now.size}명`
          : `${names(id)} 관등 ${RANKS[now.rank].name} · 정원 ${now.size}명(장수의 정원까지)`,
      );
      grown.add(id);
    } else if (now.size > was.size) {
      mates.push(`${names(id)} 정원 ${was.size} → ${now.size}명(장수의 관등이 올라)`);
      grown.add(id);
    }
  }
  if (after.level > before.level) {
    const cmd = commandShort(after.level);
    const gained = skillPointsFor(after.level) - skillPointsFor(before.level);
    out.push({
      kind: 'level',
      title: '장수 레벨이 올랐다',
      big: `Lv ${after.level}`,
      lines: [
        `Lv ${before.level} → ${after.level}`,
        ...(cmd ? [`지휘 ${cmd}`] : []),
        // 기술 점수(장수 레벨 10마다)는 능력치 화면 안에서 쓴다 — 받은 순간에 알리지 않으면 있는 줄 모른다.
        ...(gained > 0 ? [`기술 점수 +${gained} — 능력치의 장수 기술에서 쓴다`] : []),
        ...mates,
      ],
    });
  } else if (mates.length > 0) {
    out.push({ kind: 'level', title: '부하장수가 자랐다', big: grown.size === 1 ? names([...grown][0]) : `${grown.size}명`, lines: mates });
  }
  return out;
}

/** 불티 수. 폰에서도 가볍게 돌 만큼. */
const SPARKS = 28;

/** 장면이 뜬 뒤 입력을 받지 않는 시간(ms). 장면마다 다시 건다. */
const ARM_MS = 450;

/**
 * 축하 장면이 떠 있는 동안 누른 키를 어떻게 다루나. 모든 키의 기본 동작은 막는다(Tab으로 뒤의 단추에 가지 못하게).
 * - 잠긴 동안(`armed` 아님)이나 누른 채 되풀이된 키(`repeat`)는 무시한다. 처음에는 되풀이를 거르지 않아 Enter를
 *   누르고 있으면 관등 장면을 넘기자마자 레벨 장면까지 닫혔다(리뷰가 짚었다).
 * - Esc는 닫기, 그 밖은 다음 장.
 */
export function fanfareKey(key: string, repeat: boolean, armed: boolean): 'ignore' | 'next' | 'close' {
  if (!armed || repeat) return 'ignore';
  return key === 'Escape' ? 'close' : 'next';
}

/**
 * 장면들을 차례로 띄운다. 누르거나 아무 키나 누르면 다음 장, 끝나면 닫힌다. 화면 전체를 덮는 것은 이 순간이 이
 * 판의 보상이기 때문이다 — 구석의 알림으로는 성취감이 나지 않았다.
 *
 * 떠 있는 동안 뒤의 화면은 `inert`로 막는다. 처음에는 `aria-modal`만 달아, 뜨자마자 Tab 두 번과 Enter로 뒤의
 * 출정 단추가 눌렸다(리뷰가 짚었다) — 모달 표시는 조작을 막지 않는다.
 */
export function showFanfares(list: readonly Fanfare[], root: HTMLElement = document.body): void {
  if (list.length === 0) return;
  let at = 0;
  let armedAt = 0;
  const layer = el('div', { class: 'fanfare', role: 'dialog', 'aria-modal': 'true', 'aria-live': 'assertive' });
  const done = new AbortController();
  const benched: HTMLElement[] = [];
  for (const c of Array.from(root.children)) {
    if (c instanceof HTMLElement && !c.inert) {
      c.inert = true;
      benched.push(c);
    }
  }
  const active = document.activeElement;
  const back = active instanceof HTMLElement && active !== document.body ? active : null;
  const close = (): void => {
    done.abort();
    layer.remove();
    for (const c of benched) c.inert = false;
    // 초점을 돌려준다. 연 뒤 화면을 다시 그려 원래 요소가 없으면 뒤 화면 자체로 — BODY에 남으면 키보드로 이어 가기
    // 어렵다(리뷰가 짚었다). 뒤 화면의 단추에 주면 누르고 있던 Enter의 되풀이가 그 단추를 눌렀다.
    const target = back && back.isConnected ? back : root.querySelector<HTMLElement>('#screen');
    if (target && target.tabIndex < 0 && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target?.focus();
    // 닫은 키를 누르고 있으면 되풀이가 뒤 화면으로 샌다. 손을 뗄 때까지 되풀이를 삼킨다.
    const hold = new AbortController();
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.repeat) {
          e.preventDefault();
          e.stopPropagation();
        } else hold.abort();
      },
      { signal: hold.signal, capture: true },
    );
    document.addEventListener('keyup', () => hold.abort(), { signal: hold.signal, capture: true });
  };
  const show = (): void => {
    const f = list[at];
    layer.replaceChildren(scene(f, at, list.length));
    layer.setAttribute('aria-label', `${f.title}: ${f.big}`);
    (layer.querySelector('.fanfare-next') as HTMLElement | null)?.focus();
    // 뜨자마자 누른 것(철수 단추를 두 번 누름, 앞 장면을 넘긴 손)에 넘어가지 않게 장면마다 잠깐 잠근다.
    armedAt = performance.now() + ARM_MS;
  };
  const next = (): void => {
    at++;
    if (at >= list.length) close();
    else show();
  };
  layer.addEventListener(
    'click',
    () => {
      if (performance.now() >= armedAt) next();
    },
    { signal: done.signal },
  );
  document.addEventListener(
    'keydown',
    (e) => {
      // 나갈지 묻는 창이 위에 떠 있으면 키는 그 창의 것이다. 여기는 문서에서 먼저(capture) 받아, 창의 Enter·Esc가 창은
      // 두고 뒤의 축하 장면을 넘겼다(리뷰가 재현했다).
      if (leaveAsking()) return;
      e.preventDefault();
      const act = fanfareKey(e.key, e.repeat, performance.now() >= armedAt);
      if (act === 'close') close();
      else if (act === 'next') next();
    },
    { signal: done.signal, capture: true },
  );
  root.append(layer);
  show();
}

function scene(f: Fanfare, at: number, total: number): HTMLElement {
  const sparks = el('div', { class: 'fanfare-sparks', 'aria-hidden': 'true' });
  for (let k = 0; k < SPARKS; k++) {
    // 화면용이라 Math.random을 써도 된다(시뮬레이션이 아니다). 고르게 퍼지도록 각도는 나누고 흔들기만 한다.
    const ang = ((k + Math.random() * 0.6) / SPARKS) * Math.PI * 2;
    const dist = 110 + Math.random() * 120;
    const s = el('i', {});
    s.style.setProperty('--dx', `${Math.cos(ang) * dist}px`);
    s.style.setProperty('--dy', `${Math.sin(ang) * dist}px`);
    s.style.setProperty('--delay', `${Math.round(Math.random() * 180)}ms`);
    s.style.setProperty('--size', `${3 + Math.round(Math.random() * 4)}px`);
    sparks.append(s);
  }
  return el(
    'div',
    { class: `fanfare-card ${f.kind}` },
    el('div', { class: 'fanfare-rays', 'aria-hidden': 'true' }),
    sparks,
    el('div', { class: 'fanfare-title' }, f.title),
    el('div', { class: 'fanfare-big' }, f.big),
    el('div', { class: 'fanfare-lines' }, ...f.lines.map((t) => el('div', {}, t))),
    el('button', { class: 'fanfare-next', type: 'button' }, at + 1 < total ? `다음 (${at + 1}/${total})` : '확인'),
  );
}
